import { ReactNode } from 'react'
import { useLocation, Navigate } from 'react-router-dom'
import { useAccountStatus } from '@/hooks/useAccountStatus'
import { InactiveBrandMessage } from './InactiveBrandMessage'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Info } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

interface BillingGuardProps {
  children: ReactNode
}

export function BillingGuard({ children }: BillingGuardProps) {
  const { status, loading } = useAccountStatus()
  const location = useLocation()
  const navigate = useNavigate()
  
  // Always allow these paths
  const allowedPaths = ['/profile', '/settings', '/logout', '/onboarding', '/checkout/success']
  const isAllowedPath = allowedPaths.some(path => 
    location.pathname.startsWith(path)
  )
  
  
  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }
  
  // Always allow specific paths
  if (isAllowedPath) {
    return <>{children}</>
  }
  
  // No status means something went wrong
  if (!status) {
    return <Navigate to="/settings" />
  }
  
  // Handle based on effective status
  switch (status.effectiveStatus) {
    case 'active':
      // Check if accessing a specific brand
      const brandMatch = location.pathname.match(/\/brands\/([^\/]+)/)
      if (brandMatch) {
        const brandId = brandMatch[1]
        const brandAccess = status.brandAccess.find(b => b.brand.id === brandId)
        
        if (brandAccess && brandAccess.accountStatus === 'inactive' || brandAccess?.accountStatus === 'canceled') {
          return <InactiveBrandMessage brand={brandAccess.brand} />
        }
      }
      return <>{children}</>
      
    case 'needs_billing':
      // Store intended destination
      sessionStorage.setItem('billing_redirect', location.pathname)
      return <Navigate to="/settings/billing" replace />
      
    case 'inactive_brands_only':
      return (
        <div className="container mx-auto p-8 max-w-2xl">
          <Alert className="mb-6 border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
            <Info className="h-4 w-4" />
            <AlertDescription>
              <strong>Recently purchased a plan?</strong> If you just purchased a subscription and are still seeing this message, 
              please reach out to <a href="mailto:support@rainfactory.ai" className="text-blue-600 hover:text-blue-800 underline">support@rainfactory.ai</a> and 
              let them know your billing plan is failing to sync.
            </AlertDescription>
          </Alert>
          
          <Card>
            <CardHeader>
              <CardTitle>No Active Brands Available</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>All brands you have access to are currently inactive because their subscriptions have expired.</p>
              
              <div className="bg-muted p-4 rounded-lg">
                <p className="text-sm font-medium mb-2">Inactive brands you have access to:</p>
                <ul className="list-disc list-inside text-sm text-muted-foreground">
                  {status.brandAccess.map(access => (
                    <li key={access.brand.id}>
                      {access.brand.brand_name} ({access.accessLevel} access)
                    </li>
                  ))}
                </ul>
              </div>
              
              <p className="text-muted-foreground">
                To regain access, the brand owners need to reactivate their subscriptions.
              </p>
              
              <div className="flex gap-2">
                <Button onClick={() => navigate('/profile')}>
                  Go to Profile
                </Button>
                <Button variant="outline" onClick={() => navigate('/settings')}>
                  Account Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )
      
    default:
      return <Navigate to="/settings" />
  }
}